/**
 * Business Plan Page - View and download the generated business plan
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import businessPlanService, { BusinessPlanContent } from '../services/businessPlanService';

const BusinessPlanPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();

  const [plan, setPlan] = useState<BusinessPlanContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadPlan = async () => {
      try {
        const data = await businessPlanService.getBusinessPlan(sessionId!);
        setPlan(data);
        setLoading(false);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to load business plan');
        setLoading(false);
      }
    };
    loadPlan();
  }, [sessionId]);

  const handleDownload = () => {
    const url = businessPlanService.getDownloadUrl(sessionId!);
    window.open(url, '_blank');
  };

  const handleContinueToForecast = () => {
    navigate(`/financial-forecast-qa/${sessionId}`);
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h2>Loading business plan...</h2>
      </div>
    );
  }

  if (error && !plan) {
    return (
      <div style={{ padding: '40px', maxWidth: '800px', margin: '0 auto' }}>
        <div style={{
          padding: '20px',
          backgroundColor: '#f8d7da',
          color: '#721c24',
          border: '1px solid #f5c6cb',
          borderRadius: '4px',
        }}>
          <h3>Error</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1000px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
        paddingBottom: '16px',
        borderBottom: '2px solid #dee2e6',
      }}>
        <div>
          <h1 style={{ fontSize: '2rem', marginBottom: '4px' }}>Business Plan</h1>
          {plan && (
            <div style={{ fontSize: '0.9rem', color: '#666' }}>
              Generated: {new Date(plan.generated_at).toLocaleString()}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={() => navigate(`/reports/${sessionId}`)}
            style={{
              padding: '10px 20px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 'bold',
            }}
          >
            Back to Reports
          </button>
          <button
            onClick={handleDownload}
            style={{
              padding: '10px 20px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 'bold',
            }}
          >
            Download Word Document
          </button>
          <button
            onClick={handleContinueToForecast}
            style={{
              padding: '10px 20px',
              backgroundColor: '#6f42c1',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 'bold',
            }}
          >
            Continue to Financial Forecast
          </button>
        </div>
      </div>

      {/* Plan Content */}
      {plan && (
        <div style={{
          backgroundColor: 'white',
          padding: '40px',
          borderRadius: '8px',
          border: '1px solid #dee2e6',
          fontSize: '1rem',
          lineHeight: '1.7',
        }}>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              h1: ({node, ...props}) => <h1 style={{ fontSize: '2rem', marginTop: '32px', marginBottom: '16px', color: '#2c3e50' }} {...props} />,
              h2: ({node, ...props}) => <h2 style={{ fontSize: '1.6rem', marginTop: '28px', marginBottom: '12px', color: '#34495e' }} {...props} />,
              h3: ({node, ...props}) => <h3 style={{ fontSize: '1.3rem', marginTop: '24px', marginBottom: '10px', color: '#4a5568' }} {...props} />,
              p: ({node, ...props}) => <p style={{ marginBottom: '16px' }} {...props} />,
              ul: ({node, ...props}) => <ul style={{ marginBottom: '16px', paddingLeft: '24px' }} {...props} />,
              ol: ({node, ...props}) => <ol style={{ marginBottom: '16px', paddingLeft: '24px' }} {...props} />,
              li: ({node, ...props}) => <li style={{ marginBottom: '8px' }} {...props} />,
              strong: ({node, ...props}) => <strong style={{ fontWeight: 'bold', color: '#2d3748' }} {...props} />,
              blockquote: ({node, ...props}) => <blockquote style={{ borderLeft: '4px solid #6f42c1', paddingLeft: '16px', marginLeft: '0', color: '#718096', fontStyle: 'italic' }} {...props} />,
            }}
          >
            {plan.content}
          </ReactMarkdown>
        </div>
      )}
    </div>
  );
};

export default BusinessPlanPage;
